import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);
import { Injectable } from "@angular/core";
import { Subject, BehaviorSubject } from "rxjs";
import { TranslateService } from "@ngx-translate/core";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  GoldenLayoutService,
  GLPopoutConfiguration,
  GLPopoutState,
  GLPopoutUndockBehaviour,
} from "../../../golden-layout/golden-layout-service";
import { DashboardItemType } from "../../../bfx-common/models/dashboard-item-type.enum";
import { GoldenLayoutAdditionMode } from "../../../golden-layout/golden-layout-addition-mode";

import {
  InterventionDataLookupService,
  ManualPriceActionPrimaryService,
  CcyPairLookupService,
  InterventionDataPromiseLookupService,
  UserPromiseLookupService,
  ActiveUserProfilePromiseService,
  ActiveUser,
} from "shared/cubiq-services";
// Models
import {
  ManualPricingFixingTabModel,
  ManualPricingRateModel,
  ManualPricingCheckbox,
} from "../models/manual-pricing.model";
// Event models
import {
  ManualPricingCmd,
  ManualPricingCmdPayload,
} from "../models/manual-pricing-cmd-payload.model";
import { BfxDialogData } from "app/bfx-dialogs/models/bfx-dialog-data.model";
import { BfxDialogCommand } from "app/bfx-dialogs/models/bfx-dialog-command.enum";

@Injectable()
export class ManualPricingManagerService {
  /**********************************************************************/
  /* OBSERVABLE */

  private interfaceCmdEventSource = new BehaviorSubject<ManualPricingCmdPayload>(
    new ManualPricingCmdPayload(ManualPricingCmd.START),
  );
  public interfaceCmd$ = this.interfaceCmdEventSource.asObservable();

  // Observable string sources
  private blotterActionDialogSource = new Subject<BfxDialogData>();
  // Observable string streams
  public dialogCtrl$ = this.blotterActionDialogSource.asObservable();

  /**********************************************************************/
  /* DATA */
  public interfaceActive: boolean = false;
  public sortAscending: boolean = false;

  public fixingTabData: Array<ManualPricingFixingTabModel> =
    new Array<ManualPricingFixingTabModel>();

  private fallbackDefaultPrecision: number = 4;

  private activeUser: ActiveUser;

  private textTemplates: {
    title_key: string;
    warning_key: string;
  } = {
    title_key: "dealer.intervention.manual.price.title",
    warning_key: "dealer.intervention.manual.price.warning",
  };

  /**********************************************************************/
  /* CONSTRUCTOR  */

  constructor(
    private translate: TranslateService,
    private layoutService: GoldenLayoutService,
    private interventionDataService: InterventionDataLookupService,
    private interventionDataPrimaryService: InterventionDataPromiseLookupService,
    private manualPriceActionService: ManualPriceActionPrimaryService,
    private currencyPairLookup: CcyPairLookupService,
    public userService: UserPromiseLookupService,
    private activeUserService: ActiveUserProfilePromiseService,
  ) {
    this.initializeSubscriptions();

    this.activeUserService.getActiveUserData().then((activeUser: ActiveUser) => {
      this.activeUser = activeUser;
    });
  }

  private initializeSubscriptions(): void {
    this.interventionDataPrimaryService.isPrimed().then(() => {
      this.interventionDataPrimaryService.getModelUpdateSubject().subscribe((dataItem) => {
        this.processUnlockUpdate(dataItem as CubiqTS.InterventionEventImpl);
      });

      /* 	this.interventionDataPrimaryService.getModelDeleteSubject().subscribe(id => {
				logger.out.warn(`Manual-Price-Manger: Subscription DELETE `, id);
			}); */
    });
  }

  private processUnlockUpdate(dataItem: CubiqTS.InterventionEventImpl): void {
    logger.out.warn(`Manual-Price-Manger: Subscription UPDATE `, dataItem);
    if (
      dataItem instanceof CubiqTS.InterventionEventImpl &&
      dataItem.eventType === CubiqTS.InterventionEventType.FIXING_RATE &&
      dataItem.lockUserId == null
    ) {
      let fixingCodeId = this.generateFixingIdFromInterventionData(dataItem);
      let requirementId = this.getRequirementId(dataItem);
      if (typeof fixingCodeId === "string" && typeof requirementId === "string") {
        let fixingTabModel = this.fixingTabData.find((item: any) => item.id === fixingCodeId);
        if (fixingTabModel instanceof ManualPricingFixingTabModel) {
          // fixingTab found
          let fixingRateModel = fixingTabModel.rateData.find(
            (item: ManualPricingRateModel) => item.id === requirementId,
          );
          if (fixingRateModel instanceof ManualPricingRateModel) {
            // fixing rate found
            fixingRateModel.disabled = true;
            fixingRateModel.checkbox.value = false;
            fixingRateModel.checkbox.locked = true;
            if (CubiqTS.Long.isLong(dataItem.unLockUserId)) {
              if (this.activeUser.userId === dataItem.unLockUserId.toString()) {
                fixingTabModel.warning = this.translate.instant(this.textTemplates.warning_key, [
                  `Yourself (${this.activeUser.username})`,
                ]);
                this.interfaceCmdEventSource.next(
                  new ManualPricingCmdPayload(ManualPricingCmd.UPDATE_WARNING),
                );
              } else {
                this.userService
                  .getUsers([dataItem.unLockUserId.toString()])
                  .then((unlockUserDataset: Array<CubiqTS.UserImpl>) => {
                    if (Array.isArray(unlockUserDataset) && unlockUserDataset.length > 0) {
                      fixingTabModel.warning = this.translate.instant(
                        this.textTemplates.warning_key,
                        [unlockUserDataset[0].userName],
                      );
                    } else {
                      fixingTabModel.warning = this.translate.instant(
                        this.textTemplates.warning_key,
                        ["another user"],
                      );
                    }
                    this.interfaceCmdEventSource.next(
                      new ManualPricingCmdPayload(ManualPricingCmd.UPDATE_WARNING),
                    );
                  });
              }
            } else {
              fixingTabModel.warning = this.translate.instant(this.textTemplates.warning_key, [
                "another user",
              ]);
              this.interfaceCmdEventSource.next(
                new ManualPricingCmdPayload(ManualPricingCmd.UPDATE_WARNING),
              );
            }

            this.updatePriceButtonState(fixingTabModel);
          }
        }
      }
    }
  }

  /**********************************************************************/
  /* BLOTTER ACTIONS  */

  public manualPrice(data: Array<CubiqTS.FixingPriceEventLockResponseImpl>) {
    if (Array.isArray(data) && data.length > 0) {
      if (this.populateMidRateIfMissing(data)) {
        // fixes null
        let successfulLoad: boolean = this.loadInterventionData(data);
        // sort
        if (successfulLoad && Array.isArray(this.fixingTabData) && this.fixingTabData.length > 1) {
          this.fixingTabData.sort((a, b) => this.performSort(a, b, this.sortAscending));
        }
        // launch interface
        if (successfulLoad && !this.interfaceActive) {
          this.interfaceCmdEventSource.next(new ManualPricingCmdPayload(ManualPricingCmd.START));
          this.initManualPricingInterface();
        }
      }
    } else {
      logger.out.error(`Manual-Price-Manager: glInit data invalid `, data);
    }
  }

  /**********************************************************************/
  /* CORE */

  private initManualPricingInterface(): void {
    let config: GLPopoutConfiguration = new GLPopoutConfiguration();
    config.initialState = GLPopoutState.POPOUT;
    config.undockBehaviour = GLPopoutUndockBehaviour.APPLY_BOUNDING_SIZE;
    config.popoutResizable = true;
    //  config.popoutResizableDirections = ["bottom", "top"];
    config.preferredSize = { width: "auto", height: "auto" };
    config.minimumSize = { width: 630, height: 280 };

    config.data = {};
    //config.data.selectedIds = event.selectedIds;
    config.data.payload = this.fixingTabData;
    config.data.title = this.translate.instant(this.textTemplates.title_key);

    this.layoutService.launchFromDashboard(
      config.data.title,
      DashboardItemType.ManualPricing,
      false,
      GoldenLayoutAdditionMode.DEFAULT,
      config,
    );
  }

  protected loadInterventionData(
    priceLockData: Array<CubiqTS.FixingPriceEventLockResponseImpl>,
  ): boolean {
    let successfulLoad: boolean = true;
    let requirementIds: Array<string> = priceLockData
      .filter((item) => item.requirementId instanceof CubiqTS.Long)
      .map((item) => item.requirementId.toString());
    let interventionData: Array<CubiqTS.InterventionEventImpl> =
      this.interventionDataService.getPricingInterventionDataset(requirementIds);
    if (Array.isArray(interventionData) && interventionData.length > 0) {
      for (let interventionDataItem of interventionData) {
        if (this.checkFixingRateRequirementData(interventionDataItem)) {
          let priceLockDataItem = priceLockData.find((item) => {
            return (
              this.checkFixingPriceLockEvent(item) &&
              this.getRequirementId(item) === this.getRequirementId(interventionDataItem)
            );
          });
          if (priceLockDataItem instanceof CubiqTS.FixingPriceEventLockResponseImpl) {
            successfulLoad =
              successfulLoad &&
              this.processFixingRateEvent(interventionDataItem, priceLockDataItem);
          } else {
            logger.out.error(
              `ERROR: Manual-Price-Manger: loadInterventionData(): FixingPriceEventLockResponseImpl not matched to InterventionEventImpl`,
            );
            successfulLoad = false;
          }
        } else {
          logger.out.error(
            `ERROR: Manual-Price-Manger: loadInterventionData(): Invalid InterventionEventImpl object`,
          );
          successfulLoad = false;
        }
      }
      // sort
    } else {
      successfulLoad = false;
    }
    return successfulLoad;
  }

  protected processFixingRateEvent(
    interventionDataItem: CubiqTS.InterventionEventImpl,
    priceLockDataItem: CubiqTS.FixingPriceEventLockResponseImpl,
  ): boolean {
    let successfulModelGeneration: boolean = true;
    if (
      this.checkFixingRateRequirementData(interventionDataItem) &&
      this.checkFixingPriceLockEvent(priceLockDataItem) &&
      this.getRequirementId(priceLockDataItem) === this.getRequirementId(interventionDataItem)
    ) {
      let fixingCodeId = this.generateFixingIdFromInterventionData(interventionDataItem);
      let requirementId = this.getRequirementId(interventionDataItem);

      const ccyPair = this.getCcyPair(interventionDataItem.fixingRateRequirement);

      if (typeof fixingCodeId === "string" && typeof requirementId === "string") {
        try {
          // fixing tab processing
          let fixingTabModel = this.fixingTabData.find((item: any) => item.id === fixingCodeId);
          if (fixingTabModel == null) {
            fixingTabModel = new ManualPricingFixingTabModel();
            fixingTabModel.id = fixingCodeId;
            fixingTabModel.code = interventionDataItem.fixingRateRequirement.rateSourceCd;
            fixingTabModel.time =
              interventionDataItem.fixingRateRequirement.fixingTime.format("HH:mm");
            fixingTabModel.date =
              interventionDataItem.fixingRateRequirement.fixingTime.format("DD-MMM-YY");
            fixingTabModel.fixingTimestamp = parseInt(
              interventionDataItem.fixingRateRequirement.fixingTime.format("x"),
            );
            fixingTabModel.title =
              fixingTabModel.time + " " + fixingTabModel.code + " (" + fixingTabModel.date + ")";
            // create tooltip
            fixingTabModel.codeTooltip = fixingTabModel.code.length > 6 ? fixingTabModel.code : "";
            fixingTabModel.code =
              fixingTabModel.code.length > 6
                ? fixingTabModel.code.substring(0, 6) + ".."
                : fixingTabModel.code;

            this.fixingTabData.push(fixingTabModel);
          }
          // fixing rate
          let fixingRateModel = fixingTabModel.rateData.find(
            (item: ManualPricingRateModel) => item.id === requirementId,
          );
          if (fixingRateModel == null) {
            // add
            fixingRateModel = new ManualPricingRateModel(requirementId);
            fixingRateModel.fixingCode = fixingCodeId;

            fixingRateModel.base = interventionDataItem.fixingRateRequirement.baseCurrencyCd;
            fixingRateModel.nonBase = interventionDataItem.fixingRateRequirement.nonBaseCurrencyCd;
            fixingRateModel.instrument = fixingRateModel.base + fixingRateModel.nonBase;
            fixingRateModel.precision = this.getPrecisionForPair(ccyPair);
            fixingRateModel.midPrecision = this.getPrecisionForPair(ccyPair, true);
            fixingRateModel.mid = priceLockDataItem.midRate.toFixed(fixingRateModel.precision);
            fixingRateModel.bid = new CubiqTS.BigNumber(priceLockDataItem.midRate);
            fixingRateModel.offer = new CubiqTS.BigNumber(priceLockDataItem.midRate);
            // Addtional settings for fat finger tolerance checking
            fixingRateModel.ccyPair = ccyPair;
            fixingRateModel.allowTolerance = this.supportsToleranceChecking(
              ccyPair,
              priceLockDataItem.midRate,
            );
            fixingRateModel.originalMidRate = priceLockDataItem.midRate;

            fixingTabModel.rateData.push(fixingRateModel);
            this.interfaceCmdEventSource.next(new ManualPricingCmdPayload(ManualPricingCmd.ADD));
          } else {
            // update
            // Note: DO nothing with the rate values - as per KD instruction
            if (fixingRateModel.disabled) {
              fixingRateModel.disabled = false;
              fixingRateModel.checkbox.value = true;
              fixingRateModel.checkbox.locked = false;
            }
          }
          // check if warning should be removed
          this.checkAndClearWarningState(fixingTabModel);
          this.updatePriceButtonState(fixingTabModel);
        } catch (ex) {
          logger.out.error(`ERROR: Manual-Price-Manger: Exception = %O`, ex);
          successfulModelGeneration = false;
        }
      } else {
        logger.out.error(
          `ERROR: Manual-Price-Manger: Invalid input fixingCodeId or requirementId `,
        );
        successfulModelGeneration = false;
      }
    } else {
      logger.out.error(
        `ERROR: Manual-Price-Manger: Invalid input parameters `,
        interventionDataItem,
        priceLockDataItem,
      );
      successfulModelGeneration = false;
    }
    return successfulModelGeneration;
  }

  /**********************************************************************/
  /* HELPERS */

  /**
   * Get the ccy pair for the fixingRate
   * @param rateData
   * @returns
   */
  private getCcyPair(rateData: CubiqTS.FixingRateRequirementData): CubiqTS.CurrencyPairImpl {
    let retPair: CubiqTS.CurrencyPairImpl;
    if (rateData) {
      retPair = this.currencyPairLookup.getCcyPair(
        rateData.baseCurrencyCd,
        rateData.nonBaseCurrencyCd,
      );
    }
    return retPair;
  }

  /**
   * Check the pair and initial rate to determine if tolerance checking is required
   * @param ccyPair
   * @param midRate
   * @returns
   */
  private supportsToleranceChecking(ccyPair: CubiqTS.CurrencyPairImpl, midRate: CubiqTS.BigNumber) {
    return (
      midRate instanceof CubiqTS.BigNumber &&
      (midRate.isZero() ||
        (ccyPair &&
          ccyPair.fillRateToleranceUnits != null &&
          ccyPair.fillRateTolerance &&
          ccyPair.fillRateTolerance.isPositive()))
    );
  }

  /**
   * Get precision for pair
   * @param pair
   * @param midPrecision When true, will check additional mid rounding precision
   * @returns
   */
  private getPrecisionForPair(
    pair: CubiqTS.CurrencyPairImpl,
    midPrecision: boolean = false,
  ): number {
    if (pair instanceof CubiqTS.CurrencyPairImpl) {
      if (midPrecision) {
        return typeof pair.bmSpotPrecision === "number" &&
          typeof pair.additionalMidRoundingPrecision === "number"
          ? pair.bmSpotPrecision + pair.additionalMidRoundingPrecision
          : this.fallbackDefaultPrecision + 1;
      } else {
        return typeof pair.bmSpotPrecision === "number"
          ? pair.bmSpotPrecision
          : this.fallbackDefaultPrecision;
      }
    }
    return this.fallbackDefaultPrecision;
  }

  /* Handles the situation where midRate is not available and null is returned from the server */
  private populateMidRateIfMissing(
    priceLockData: Array<CubiqTS.FixingPriceEventLockResponseImpl>,
  ): boolean {
    if (Array.isArray(priceLockData) && priceLockData.length > 0) {
      for (let fixingPriceLockItem of priceLockData) {
        if (fixingPriceLockItem instanceof CubiqTS.FixingPriceEventLockResponseImpl) {
          if (fixingPriceLockItem.midRate == null) {
            fixingPriceLockItem.midRate = new CubiqTS.BigNumber(0);
          }
        } else {
          return false;
        }
      }
      return true;
    } else {
      return false;
    }
  }

  private checkFixingRateRequirementData(dataItem: CubiqTS.InterventionEventImpl): boolean {
    return (
      dataItem instanceof CubiqTS.InterventionEventImpl &&
      dataItem.fixingRateRequirement instanceof CubiqTS.FixingRateRequirementDataImpl &&
      dataItem.fixingRateRequirement.requirementId instanceof CubiqTS.Long &&
      typeof dataItem.fixingRateRequirement.rateSourceCd == "string" &&
      dataItem.fixingRateRequirement.fixingTime != null &&
      typeof dataItem.fixingRateRequirement.baseCurrencyCd === "string" &&
      dataItem.fixingRateRequirement.baseCurrencyCd.length === 3 &&
      typeof dataItem.fixingRateRequirement.nonBaseCurrencyCd === "string" &&
      dataItem.fixingRateRequirement.nonBaseCurrencyCd.length === 3
    );
  }

  private checkFixingPriceLockEvent(
    fixingPriceLock: CubiqTS.FixingPriceEventLockResponseImpl,
  ): boolean {
    return (
      fixingPriceLock instanceof CubiqTS.FixingPriceEventLockResponseImpl &&
      fixingPriceLock.requirementId instanceof CubiqTS.Long &&
      fixingPriceLock.midRate instanceof CubiqTS.BigNumber
    );
  }

  private getRequirementId(
    dataItem: CubiqTS.InterventionEventImpl | CubiqTS.FixingPriceEventLockResponseImpl,
  ): string {
    if (
      dataItem instanceof CubiqTS.InterventionEventImpl &&
      this.checkFixingRateRequirementData(dataItem)
    ) {
      return dataItem.fixingRateRequirement.requirementId.toString();
    } else if (
      dataItem instanceof CubiqTS.FixingPriceEventLockResponseImpl &&
      this.checkFixingPriceLockEvent(dataItem)
    ) {
      return dataItem.requirementId.toString();
    } else {
      return null;
    }
  }

  public generateFixingIdFromInterventionData(
    dataItem: CubiqTS.InterventionEventImpl,
  ): string | null {
    if (this.checkFixingRateRequirementData(dataItem)) {
      let code = dataItem.fixingRateRequirement.rateSourceCd;
      let moment = dataItem.fixingRateRequirement.fixingTime;
      if (typeof code === "string" && moment != null) {
        // instanceof CubiqTS.moment.Moment
        try {
          let date: string = moment.format("DD-MMM-YY");
          let time: string = moment.format("HH:mm");
          return code + ":" + date + ":" + time;
        } catch (ex) {
          logger.out.error(`ERROR: Manual-Price-Manger: generateFixingIdFromCodeMoment(): ${ex}`);
          return null;
        }
      }
    }
    return null;
  }

  private performSort(
    a: ManualPricingFixingTabModel,
    b: ManualPricingFixingTabModel,
    asc: boolean = false,
  ): number {
    try {
      let aDateTime = a.fixingTimestamp;
      let bDateTime = b.fixingTimestamp;
      if (aDateTime === bDateTime) {
        return a.code.localeCompare(b.code);
      }
      return asc ? (aDateTime > bDateTime ? 1 : -1) : aDateTime > bDateTime ? -1 : 1;
    } catch (ex) {
      logger.out.error(`ERROR: Manual-Price-Manger: sorting exception: ${ex}`);
      return 0;
    }
  }

  private checkAndClearWarningState(fixingTabModel: ManualPricingFixingTabModel): void {
    if (fixingTabModel instanceof ManualPricingFixingTabModel) {
      // warning state
      if (fixingTabModel.warning != null) {
        let findDisabled = fixingTabModel.rateData.find((item) => item.disabled);
        if (findDisabled == null) {
          fixingTabModel.warning = null;
          this.interfaceCmdEventSource.next(
            new ManualPricingCmdPayload(ManualPricingCmd.UPDATE_WARNING),
          );
        }
      }
    }
  }

  private updatePriceButtonState(fixingTabModel: ManualPricingFixingTabModel): void {
    if (
      fixingTabModel instanceof ManualPricingFixingTabModel &&
      Array.isArray(fixingTabModel.rateData) &&
      fixingTabModel.rateData.length > 0
    ) {
      let findEnabled = fixingTabModel.rateData.find((item) => !item.disabled);
      if (fixingTabModel.nothingToPrice && findEnabled != null) {
        // button is already disabled, there are enabled items
        fixingTabModel.nothingToPrice = false;
        fixingTabModel.checkboxFixing.locked = false;
        this.interfaceCmdEventSource.next(
          new ManualPricingCmdPayload(ManualPricingCmd.UPDATE_PRICE_BTN),
        );
      } else if (!fixingTabModel.nothingToPrice && findEnabled == null) {
        // button is not disabled, there are NO enabled items
        fixingTabModel.nothingToPrice = true;
        fixingTabModel.checkboxFixing.locked = true;
        this.interfaceCmdEventSource.next(
          new ManualPricingCmdPayload(ManualPricingCmd.UPDATE_PRICE_BTN),
        );
      }
    }
  }

  /**********************************************************************/
  /* INTERFACE ACTIONS  */
  public interfaceOnline() {
    this.interfaceActive = true;
  }

  public interfaceOffline() {
    this.interfaceActive = false;
  }

  public sortData(asc?: boolean): void {
    if (typeof asc === "boolean") {
      this.sortAscending = asc;
    }

    if (Array.isArray(this.fixingTabData) && this.fixingTabData.length > 1) {
      this.fixingTabData.sort((a, b) => this.performSort(a, b, this.sortAscending));
    }
  }

  public closeWarning(tabId: string): void {
    let tabDataModel = this.fixingTabData.find(
      (item: ManualPricingFixingTabModel) => item.id === tabId,
    );
    if (tabDataModel instanceof ManualPricingFixingTabModel) {
      tabDataModel.warning = null;
      this.interfaceCmdEventSource.next(
        new ManualPricingCmdPayload(ManualPricingCmd.UPDATE_WARNING),
      );
    } else {
      logger.out.error(
        `ERROR: Manual-Price-Manager: closeWarning(): Tab data model not found (${tabId})`,
      );
    }
  }

  public closeAll(): void {
    if (Array.isArray(this.fixingTabData) && this.fixingTabData.length > 0) {
      this.executeUnlock(this.fixingTabData);
      this.executeExit();
    }
  }

  /* not in use - for now ui cancel button behaves as exit */
  public cancel(tabId: string): void {
    //logger.out.info(`Manual-Price-Manger: cancel() = `, tabId);
    let tabDataModel = this.fixingTabData.find(
      (item: ManualPricingFixingTabModel) => item.id === tabId,
    );
    if (tabDataModel instanceof ManualPricingFixingTabModel) {
      this.executeUnlock(tabDataModel);
      this.executeCloseTab(tabDataModel.id);
    } else {
      logger.out.error(`ERROR: Manual-Price-Manager: price(): Tab data model not found (${tabId})`);
    }
  }

  public price(tabId: string): void {
    //logger.out.info(`Manual-Price-Manger: price() = `, tabId);
    const tabDataModel = this.fixingTabData.find(
      (item: ManualPricingFixingTabModel) => item.id === tabId,
    );
    if (tabDataModel instanceof ManualPricingFixingTabModel) {
      const failingRates: ManualPricingRateModel[] = this.getPricesOutsideTolerance(tabDataModel);
      if (failingRates.length > 0) {
        logger.out.info("Got failing rates");
        this.showToleranceDialog(tabDataModel, failingRates);
      } else {
        this.executePrice(tabDataModel);
      }
    } else {
      logger.out.error(`ERROR: Manual-Price-Manager: price(): Tab data model not found (${tabId})`);
    }
  }

  /**
   * Get all prices in the fixing tab that are outside of tolerance
   * @param tabDataModel
   * @returns List of all prices that outside of tolerance
   */
  private getPricesOutsideTolerance(
    tabDataModel: ManualPricingFixingTabModel,
  ): ManualPricingRateModel[] {
    const toleranceErrors: ManualPricingRateModel[] = [];
    for (let rateDataItem of tabDataModel.rateData) {
      if (
        rateDataItem instanceof ManualPricingRateModel &&
        rateDataItem.allowTolerance &&
        rateDataItem.validBidOffer() &&
        rateDataItem.checkbox instanceof ManualPricingCheckbox &&
        rateDataItem.checkbox.value &&
        !rateDataItem.disabled
      ) {
        // Check the bid distance
        const bidFail = this.checkFailTolerance(
          rateDataItem.ccyPair,
          rateDataItem.originalMidRate,
          rateDataItem.bid,
        );
        const offerFail = this.checkFailTolerance(
          rateDataItem.ccyPair,
          rateDataItem.originalMidRate,
          rateDataItem.offer,
        );

        if (bidFail || offerFail) {
          // WE only add a single tolerance error - bid has preference
          const badRate = bidFail ? rateDataItem.bid : rateDataItem.offer;
          this.addToleranceError(rateDataItem, rateDataItem.originalMidRate, badRate, bidFail);
          toleranceErrors.push(rateDataItem);
        }
      }
    }

    // // We now have an array of all prices that have failed
    return toleranceErrors;
  }

  /**
   * Update the pricing model with a tolerance fail error message
   * @param pricingItem Contains the pair, precisions
   * @param marketRate
   * @param itemRate
   * @param bid Is failure on Bid side of rate?
   */
  private addToleranceError(
    pricingItem: ManualPricingRateModel,
    marketRate: CubiqTS.BigNumber,
    itemRate: CubiqTS.BigNumber,
    bid: boolean,
  ): void {
    const ccyPair: CubiqTS.CurrencyPairImpl = pricingItem.ccyPair;
    const marketRateLabel = marketRate.isZero()
      ? "UNAVAILABLE"
      : marketRate.toFixed(pricingItem.midPrecision);
    const units: string =
      ccyPair.fillRateToleranceUnits == CubiqTS.UnitType.PERCENT ? "%" : " pips";

    const sideStr = this.translate.instant(
      "dealer.intervention.manual.price.label." + (bid ? "bid" : "offer"),
    );
    const errorStr: string = this.translate.instant("dealer.intervention.manual.price.tolerance", [
      itemRate.toFixed(pricingItem.precision),
      ccyPair.fillRateTolerance.toString() + units,
      marketRateLabel,
      pricingItem.instrument,
      sideStr,
    ]);
    pricingItem.toleranceError = errorStr;
    logger.out.info(`mpm-s - Pricing tolerance error: ${errorStr}`);
  }

  /**
   * Check that distance between the mid rate and specifed rate is less than the allowed tolerance defined for the pair
   *
   * @param pair
   * @param midRate
   * @param rate
   * @returns true if the rate fails the distance check
   */
  private checkFailTolerance(
    pair: CubiqTS.CurrencyPairImpl,
    midRate: CubiqTS.BigNumber,
    rate: CubiqTS.BigNumber,
  ): boolean {
    if (!midRate || midRate.isZero()) {
      // no rate so we flag the order for tolerance fail
      return true;
    }

    const toleranceUnits = pair.fillRateToleranceUnits ?? CubiqTS.UnitType.PERCENT;
    let maxAllowedDiff: CubiqTS.BigNumber;
    if (toleranceUnits == CubiqTS.UnitType.PERCENT) {
      maxAllowedDiff = midRate.times(pair.fillRateTolerance).dividedBy(100);
    } else {
      const pips: number = pair.rateDecimalsQty;
      maxAllowedDiff = pair.fillRateTolerance.times(Math.pow(10, -pips));
    }

    const marketTolerance: CubiqTS.BigNumber = midRate.minus(rate).abs();
    return marketTolerance.isGreaterThan(maxAllowedDiff);
  }

  private showToleranceDialog(
    tabDataModel: ManualPricingFixingTabModel,
    failedPrices: ManualPricingRateModel[],
  ): void {
    let errorMessage: string;

    if (failedPrices.length == 1) {
      errorMessage = failedPrices[0].toleranceError;
    } else {
      // Determine the total count that will be submitted (may be different from total in the tab)
      const totalCount = tabDataModel.rateData.filter(
        (rateDataItem) =>
          rateDataItem.checkbox instanceof ManualPricingCheckbox &&
          rateDataItem.checkbox.value &&
          !rateDataItem.disabled,
      ).length;
      errorMessage = this.translate.instant("dealer.intervention.manual.price.tolerance.multiple", [
        failedPrices.length,
        totalCount,
      ]);
    }
    const dialogData = new BfxDialogData(
      BfxDialogCommand.DIALOG_ORDER_FILL_TOLERANCE_WARNING_OPEN,
      null,
      null,
      {
        messages: [errorMessage],
        title: this.translate.instant("tolerance.warning.title"),
        callback: () => {
          const currentDataModel = this.fixingTabData.find(
            (item: ManualPricingFixingTabModel) => item.id === tabDataModel.id,
          );
          if (currentDataModel == tabDataModel) {
            this.executePrice(tabDataModel);
          } else {
            logger.out.warn("Pricing model has changed since dialog confirmation was raised");
          }
        },
        callbackNegative: () => {
          logger.out.info("Price submission rejectd by user");
        },
      },
    );
    this.sendDialogCommand(dialogData);
  }

  /**
   * Launches a new dialog with a given dialog data
   * @param data the dialog data
   */
  public sendDialogCommand(data: BfxDialogData): void {
    this.blotterActionDialogSource.next(data);
  }

  /**********************************************************************/
  /* MANAGER INTERFACE ACTION HANDLERS */

  private executePrice(tabData: ManualPricingFixingTabModel): void {
    if (tabData instanceof ManualPricingFixingTabModel) {
      let ratesToBePriced: Array<CubiqTS.ManualPriceImpl> = new Array<CubiqTS.ManualPriceImpl>();
      let rateIdsToBeUnlocked: Array<string> = new Array<string>();
      for (let rateDataItem of tabData.rateData) {
        if (rateDataItem instanceof ManualPricingRateModel && rateDataItem.validBidOffer()) {
          if (
            rateDataItem.checkbox instanceof ManualPricingCheckbox &&
            rateDataItem.checkbox.value &&
            !rateDataItem.disabled
          ) {
            let priceItem: CubiqTS.ManualPriceImpl = new CubiqTS.ManualPriceImpl();
            priceItem.requirementId = new CubiqTS.Long(parseFloat(rateDataItem.id));
            priceItem.bid = new CubiqTS.BigNumber(rateDataItem.bid);
            priceItem.offer = new CubiqTS.BigNumber(rateDataItem.offer);
            ratesToBePriced.push(priceItem);
          } else if (!rateDataItem.disabled) {
            rateIdsToBeUnlocked.push(rateDataItem.id);
          }
        } else {
          logger.out.error(
            `ERROR: Manual-Price-Manger: executePrice(): Invalid rateData item %O`,
            rateDataItem,
          );
        }
      }
      // price action
      if (ratesToBePriced.length > 0) {
        this.issuePrice(ratesToBePriced).then((pricedItemIds: Array<string>) => {
          if (Array.isArray(pricedItemIds) && pricedItemIds.length > 0) {
            if (tabData.rateData.length === pricedItemIds.length) {
              this.executeCloseTab(tabData.id);
            } else {
              for (let pricedItemId of pricedItemIds) {
                let rateIndex = tabData.rateData.map((item) => item.id).indexOf(pricedItemId);
                if (rateIndex >= 0) {
                  tabData.rateData.splice(rateIndex, 1);
                } else {
                  logger.out.error(
                    `ERROR: Manual-Price-Manger: executePrice():Can not find priced item %O`,
                    pricedItemId,
                  );
                }
              }
              // fallback
              if (tabData.rateData.length == 0) {
                this.executeCloseTab(tabData.id);
              }
              this.interfaceCmdEventSource.next(
                new ManualPricingCmdPayload(ManualPricingCmd.UPDATE),
              );
            }
          }
        });
      }
      // unlock action -> not in use since OMS-32164
      /* if(rateIdsToBeUnlocked.length === 0){
				this.issueUnlock(rateIdsToBeUnlocked);
			} */
    }
  }

  private executeUnlock(
    data: Array<ManualPricingFixingTabModel> | ManualPricingFixingTabModel,
  ): void {
    if (data instanceof ManualPricingFixingTabModel) {
      data = new Array<ManualPricingFixingTabModel>(data);
    }

    if (Array.isArray(data) && data.length > 0 && data[0] instanceof ManualPricingFixingTabModel) {
      let allIdsToBeUnlocked: Array<string> = new Array<string>();
      for (let tabData of data) {
        if (
          tabData instanceof ManualPricingFixingTabModel &&
          Array.isArray(tabData.rateData) &&
          tabData.rateData.length > 0
        ) {
          let tabUnlockIds: Array<string> = tabData.rateData
            .map((item) =>
              item instanceof ManualPricingRateModel &&
              typeof item.id === "string" &&
              !item.disabled
                ? item.id
                : null,
            )
            .filter((item) => item != null && item !== ""); // clean up (just in case)
          allIdsToBeUnlocked = allIdsToBeUnlocked.concat(tabUnlockIds);
        }
      }
      // unlock action
      if (allIdsToBeUnlocked.length > 0) {
        this.issueUnlock(allIdsToBeUnlocked);
      }
    } else {
    }
  }

  private executeCloseTab(tabId: string): void {
    //logger.out.info(`Manual-Price-Manger: closeTab() = [${tabId}]`);
    if (Array.isArray(this.fixingTabData) && this.fixingTabData.length > 0) {
      let tabIndex = this.fixingTabData.map((item) => item.id).indexOf(tabId);
      if (tabIndex >= 0) {
        this.fixingTabData.splice(tabIndex, 1);
        if (this.fixingTabData.length === 0) {
          this.executeExit();
        } else {
          this.interfaceCmdEventSource.next(
            new ManualPricingCmdPayload(ManualPricingCmd.ACTIVATE, this.fixingTabData[0].id),
          );
        }
      } else {
        logger.out.error(`ERROR: Manual-Price-Manger: closeTab(): Tab not found ${tabId}`);
      }
    } else {
      logger.out.error(
        `ERROR: Manual-Price-Manger: closeTab(): No data model -> Closing interface`,
      );
      this.executeExit();
    }
  }

  private executeExit(): void {
    //logger.out.info(`Manual-Price-Manger: exit()`);

    this.fixingTabData = new Array<ManualPricingFixingTabModel>();
    if (this.interfaceActive) {
      this.interfaceCmdEventSource.next(new ManualPricingCmdPayload(ManualPricingCmd.EXIT));
    }
  }

  /**********************************************************************/
  /* CUBIQ SERVICE ACTIONS */

  private issueUnlock(unlockIds: Array<string>): void {
    if (Array.isArray(unlockIds) && unlockIds.length > 0) {
      //logger.out.info(`Manual-Price-Manger: issueUnlock()`, unlockIds.toString());
      this.manualPriceActionService.issueManualPriceUnlock(unlockIds).then(
        (response) => {
          if (response !== null) {
            logger.out.error(
              `ERROR: Manual-Price-Manger: issueUnlock(): Unexpected unlock response %O`,
              response,
            );
          }
        },
        (rejection) => {
          logger.out.error(
            `ERROR: Manual-Price-Manger: issueUnlock(): Operation rejected %O`,
            rejection,
          );
        },
      );
    } else {
      logger.out.error(
        `ERROR: Manual-Price-Manger: issueUnlock(): Invalid input array  %O`,
        unlockIds,
      );
    }
  }

  private issuePrice(priceItems: Array<CubiqTS.ManualPriceImpl>): Promise<Array<string>> {
    return new Promise<any>((resolve, reject) => {
      if (Array.isArray(priceItems) && priceItems.length > 0) {
        let checkedPriceItems = priceItems.filter(
          (item) => item instanceof CubiqTS.ManualPriceImpl,
        );
        if (
          Array.isArray(checkedPriceItems) &&
          checkedPriceItems.length > 0 &&
          checkedPriceItems.length === priceItems.length
        ) {
          this.manualPriceActionService.issueManualPrice(checkedPriceItems).then(
            (response) => {
              logger.out.info(`ERROR: Manual-Price-Manger: issuePrice(): response = `, response);
              if (
                response instanceof CubiqTS.StandardReportImpl &&
                Array.isArray(response.results) &&
                response.results.length > 0
              ) {
                let pricedDataset = response.results.filter((item) => {
                  return (
                    item instanceof CubiqTS.ManualPriceImpl &&
                    item.errors instanceof CubiqTS.ErrorsImpl &&
                    Array.isArray(item.errors.errorMessages) &&
                    item.errors.errorMessages.length === 0
                  );
                });

                resolve(
                  Array.isArray(pricedDataset) && pricedDataset.length > 0
                    ? pricedDataset.map((item: CubiqTS.ManualPriceImpl) =>
                        item.requirementId.toString(),
                      )
                    : [],
                );
              } else {
                reject(`Invalid standard report returned from the server`);
              }
            },
            (rejection) => {
              logger.out.error(
                `ERROR: Manual-Price-Manger: issuePrice(): Operation rejected %O`,
                rejection,
              );
              reject(rejection);
            },
          );
        } else {
          let errorMsg: string = "Invalid object types in input array";
          logger.out.error(`ERROR: Manual-Price-Manger: issuePrice(): ${errorMsg} %O`, priceItems);
          reject(errorMsg);
        }
      } else {
        let errorMsg: string = "Invalid input array";
        logger.out.error(`ERROR: Manual-Price-Manger: issuePrice(): ${errorMsg} %O`, priceItems);
        reject(errorMsg);
      }
    });
  }
}
